import { Button } from '@mui/material';
import { Form, FormikProps, withFormik } from 'formik';
import React, { useMemo } from 'react';
import { IAddUserFormValues } from '../../../../@types/modelV2/rights/user/addUserFormValues';
import UserModelHelper from '../../../../@types/modelV2/rights/user/userModelHelper';
import { IUserUpsert } from '../../../../@types/modelV2/rights/user/userUpsert';
import { useGetUsers } from '../../../../hooks/query/rights/userQueries';
import EmailHelper from '../../../../services/emailHelper';
import GeneralFunctions from '../../../../store/general/functions';
import PillButton from '../../../customComponentV2/button/PillButton';
import FormCheckbox from '../../../customComponentV2/input/FormCheckbox';
import FormTextInput from '../../../customComponentV2/input/FormTextInput';

interface IUserAddDialogProps {
    isLoading : boolean;
    onClose : () => void;
    onSubmit : (upsert : IUserUpsert) => void;
    initialFormValues : IAddUserFormValues;
}

type AddUserDialogPropsType = IUserAddDialogProps & FormikProps<IAddUserFormValues>;

const AddUserDialog = (props : AddUserDialogPropsType) : React.ReactElement => {
    const { isLoading: isLoadingUsers, data: users } = useGetUsers();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const closeAddUserDialog = () : void => {
        props.onClose();
    };

    const onNewUserSubmit = () : void => {
        if (users?.find(x => x.isActive && x.email === props.values.email)) {
                GeneralFunctions.generalShowErrorSnackbarV2('Email already exists!');
            return;
        }

        if (users?.find(x => x.isActive && props.values.employeeNumber !== '' && x.employeeNumber === props.values.employeeNumber.toUpperCase())) {
            GeneralFunctions.generalShowErrorSnackbarV2('Employee number already exists!');
            return;
        }

        const upsert : IUserUpsert = UserModelHelper.createAddUserUpsert(props.values);
        
        props.onSubmit(upsert);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return props.isLoading
        || isLoadingUsers;
    }, [
        props.isLoading,
        isLoadingUsers,
    ]);

    const isSubmitEnabled = useMemo<boolean>(() => {
        return (
            !!props.values.name 
            && !!props.values.email 
            && !!props.values.password 
            && EmailHelper.validateFormat(props.values.email)
            && EmailHelper.validateDomain(props.values.email)
        ); 
    }, [props.values.name, props.values.email, props.values.password ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return <Form>
        <div className={'fdc hfill p20 w700'}>
            <div className={'fdr flx1 aic mb10'}>
                <FormTextInput 
                    className={'flx1'}
                    label={'Name'}                       
                    name={'name'}
                />
                <div className={'w10'} />
                <FormTextInput
                    className={'flx1'}
                    label={'Email'}
                    name={'email'}
                />
            </div>
            <div className={'fdr flx1 aic mb10'}>
                <FormTextInput
                    className={'flx1'}
                    label={'Employee Number'}
                    name={'employeeNumber'}
                />
                <div className={'w10'} />
                <FormTextInput
                    className={'flx1'}
                    label={'Password'}
                    name={'password'}
                    type={'password'}
                />
            </div>
            <div className={'fdr jcsb mt10'}>
                <div className={'fdr aic'}>
                    <Button
                        className={'fwb mr5 h35'}
                        variant='text'
                        disabled={isLoading}
                        onClick={closeAddUserDialog}>
                        Cancel
                    </Button>
                    <PillButton
                        text={'Submit'}
                        className={'pl30 pr30 h35 ml5'}
                        onClick={onNewUserSubmit}
                        disabled={isLoading || !isSubmitEnabled} 
                        color={'secondary'}
                        size={'small'}
                    ></PillButton>
                </div>
            </div>
        </div>
    </Form>;
};

export default withFormik<IUserAddDialogProps, IAddUserFormValues>({
    mapPropsToValues: props => props.initialFormValues,
    handleSubmit: () => null,
    enableReinitialize: true,
    validationSchema: UserModelHelper.addUserFormSchema,
})(AddUserDialog);
